/**
 * Confidentiality labels on clinical records — the segmentation seam.
 *
 * Decided 2026-08-17 (`docs/roadmap.md` §9): no behavioral-health or SUD program is planned,
 * but an EMR collects that data incidentally — a primary-care note mentioning alcohol use, a
 * depression screening score, a medication whose only indication is opioid use disorder. So we
 * carry a label on every clinical record from the first migration and teach every read, export,
 * and audit path to consult it, while leaving the 42 CFR Part 2 consent and redisclosure
 * workflow unbuilt.
 *
 * The label is multi-reason rather than a Part 2 boolean on purpose. HIV status, genetic
 * results, reproductive health, and minor-consent records carry their own state-law
 * restrictions entirely independent of Part 2, so the field earns its place even with Part 2
 * fully out of scope.
 *
 * Vocabulary note: these reason codes intentionally mirror concepts from HL7's Data
 * Segmentation for Privacy (DS4P) work and the value sets FHIR carries in
 * `Resource.meta.security`, rather than being invented here. The exact code systems and
 * bindings must be confirmed against current HL7/FHIR guidance when the FHIR layer is built
 * (Phase 6) — this module is the internal representation, not a claim about wire format.
 */

/**
 * Why a record is restricted. A record may carry more than one.
 *
 * Extend deliberately: each value implies real handling rules somewhere, and an unused reason
 * is worse than no reason because it looks enforced.
 */
export const RESTRICTION_REASONS = [
  /** Substance use disorder. The 42 CFR Part 2 trigger, if the source is a federally-assisted program. */
  'substanceUse',
  /** Mental / behavioral health. */
  'behavioralHealth',
  /** HIV/AIDS status, which many states restrict specifically. */
  'hivStatus',
  /** Genetic testing and results. */
  'genetic',
  /** Reproductive and sexual health. */
  'reproductiveHealth',
  /** Care a minor consented to themselves, which the guardian may not be entitled to see. */
  'minorConsent',
  /** Employee, public figure, or otherwise access-sensitive patient. Not about the data's subject matter. */
  'sensitivePatient',
] as const;

export type RestrictionReason = (typeof RESTRICTION_REASONS)[number];

/**
 * The label carried by a clinical record.
 *
 * `reasons` empty means ordinary chart data. That is the common case and must stay cheap — the
 * whole design fails if labelling makes the normal path expensive.
 */
export interface ConfidentialityLabel {
  readonly reasons: readonly RestrictionReason[];
}

export const UNRESTRICTED: ConfidentialityLabel = { reasons: [] };

export function isRestrictionReason(value: string): value is RestrictionReason {
  return (RESTRICTION_REASONS as readonly string[]).includes(value);
}

/**
 * Build a label, discarding unknown reasons and de-duplicating.
 *
 * Unknown reasons are dropped rather than throwing because this parses data that may have been
 * written by an older or newer version of the schema. Dropping is the safe direction only
 * because callers must treat the *result* as authoritative — see `isRestricted`, which is what
 * access decisions consult, and which errs toward restricting.
 */
export function labelOf(reasons: readonly string[]): ConfidentialityLabel {
  const seen = new Set<RestrictionReason>();
  for (const reason of reasons) {
    if (isRestrictionReason(reason)) seen.add(reason);
  }
  return { reasons: [...seen] };
}

/** Does this record carry any restriction at all? */
export function isRestricted(label: ConfidentialityLabel): boolean {
  return label.reasons.length > 0;
}

/**
 * Is this record visible to a viewer permitted the given set of reasons?
 *
 * Fails closed in the way that matters: a record restricted for a reason the viewer is not
 * permitted is hidden, and a viewer permitted nothing sees only unrestricted records. There is
 * no "permitted everything" shortcut here on purpose — a break-glass path must be an explicit,
 * audited decision at the call site (backlog T7), never a flag threaded through this function.
 */
export function isVisibleTo(
  label: ConfidentialityLabel,
  permittedReasons: readonly RestrictionReason[],
): boolean {
  if (!isRestricted(label)) return true;
  const permitted = new Set<RestrictionReason>(permittedReasons);
  return label.reasons.every((reason) => permitted.has(reason));
}

/**
 * The C4b seam: filter an arbitrary list of records down to the ones visible under
 * `permittedReasons`.
 *
 * `chartExportService.ts`'s chart-PDF export (`BACKLOG.md` C22) is this function's first real
 * consumer — every `*Section` builder in that file runs its own record list through this (by way
 * of `selectExportRows`, which layers the export's own date-range filter on top) before any
 * content reaches the assembled `PdfDocumentSpec`. **Every future export path — a FHIR response,
 * a CCDA document, an EHI export job, a referral packet, a claims attachment (`BACKLOG.md`
 * N1–N4) — must run its own record set through this before serializing it**, the same way
 * `chartExportService.ts` already does, instead of hand-rolling a second "map each record to a
 * label, call `isVisibleTo`, filter" loop. That second loop is exactly the shape of the bug this
 * seam exists to make structurally hard to repeat: an earlier version of
 * `chartExportService.ts`'s own chart-access gate once passed `[]` in place of a patient's real
 * `confidentialityReasons`, which silently treated every restricted patient as unrestricted.
 * Calling this one function, instead of re-deriving the filter loop by hand, is what a review of
 * the next export path can actually check for.
 *
 * `confidentialityReasonsOf` reads the *raw* string array a record carries straight off its own
 * shape (a DB row's `confidentialityReasons: string[]` column, a FHIR resource's own extension, a
 * CCDA section's source field) — this function calls `labelOf` on the result itself, so a caller
 * never needs an intermediate `ConfidentialityLabel` construction step of its own.
 *
 * `permittedReasons` defaults to `[]`, the same conservative default every read path in this
 * codebase uses today (`server/src/clinical/access.ts`'s `evaluateChartAccess`/`isRecordVisible`)
 * until a real permitted-reasons resolution mechanism exists — see `isVisibleTo`'s own doc comment
 * for why that is the safe default rather than a placeholder cut corner. Passing it explicitly is
 * how a future caller with a genuine permission set (once one exists) widens visibility without
 * this function itself changing.
 */
export function filterVisibleRecords<T>(
  records: readonly T[],
  confidentialityReasonsOf: (record: T) => readonly string[],
  permittedReasons: readonly RestrictionReason[] = [],
): readonly T[] {
  return records.filter((record) =>
    isVisibleTo(labelOf(confidentialityReasonsOf(record)), permittedReasons),
  );
}

/**
 * Does this label require the 42 CFR Part 2 consent workflow that we have deliberately not
 * built?
 *
 * Part 2 turns on *provenance* — whether the information came from a federally-assisted SUD
 * program — not merely on subject matter, which is why this takes the source flag rather than
 * inferring from `substanceUse` alone. Today this should always be `false` in production, since
 * we serve no such program. It exists so the tripwire is expressible in code: if it ever returns
 * `true`, the deferred consent machinery (backlog C4d) is required, not optional.
 */
export function requiresPart2Consent(
  label: ConfidentialityLabel,
  fromFederallyAssistedSudProgram: boolean,
): boolean {
  return fromFederallyAssistedSudProgram && label.reasons.includes('substanceUse');
}
